import React, { useState, useEffect } from "react";
import { User, ClipboardList } from "lucide-react";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { formatCPF, formatPhoneBRLive, isoToBR, brToISO, maskDateBRLive } from "../../lib/utils";

const estadoCivilOptions = [
  { value: "Solteiro(a)" },
  { value: "Casado(a)" },
  { value: "Divorciado(a)" },
  { value: "Viúvo(a)" },
];

export function PersonalDataForm({
  userData,
  handleUserDataChange,
  errors,
  isAdminMode,
  setIsFalecido,
  section = "all", // "all" | "top" | "bottom"
}) {
  const showTop = section === "all" || section === "top";
  const showBottom = section === "all" || section === "bottom";

  const [dateDisplay, setDateDisplay] = useState(() => isoToBR(userData.data_nascimento) || "");
  useEffect(() => {
    setDateDisplay(isoToBR(userData.data_nascimento) || "");
  }, [userData.data_nascimento]);

  return (
    <div className="mb-6 md:mb-10">
      {showTop && (
        <div className="flex items-center gap-3 mb-4 md:mb-6">
          <div className="w-7 h-7 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-lg bg-gradient-to-br from-blue-900 to-blue-700 flex items-center justify-center shadow-md shrink-0">
            <User className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          </div>
          <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-foreground">
            Dados Pessoais
          </h2>
        </div>
      )}

      {section === "bottom" && (
        <div className="flex items-center gap-3 mb-4 md:mb-6">
          <div className="w-7 h-7 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-lg bg-gradient-to-br from-indigo-900 to-indigo-700 flex items-center justify-center shadow-md shrink-0">
            <ClipboardList className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          </div>
          <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-foreground">
            Informações Complementares
          </h2>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">
        {showTop && (
          <>
            <div>
              <Label htmlFor="nome" className="text-sm font-medium text-foreground">
                Nome Completo <span className="text-red-500">*</span>
              </Label>

              <Input
                id="nome"
                value={userData.nomeCompleto}
                onChange={(e) => handleUserDataChange("nomeCompleto", e.target.value)}
                className={`mt-2 border-2 transition-colors h-11 focus:ring-2 focus:ring-blue-800/20 ${
                  errors.nomeCompleto
                    ? "border-red-400 focus:border-red-600"
                    : "border-border focus:border-blue-800"
                }`}
              />

              {errors.nomeCompleto && (
                <p className="mt-1 text-xs text-red-600">{errors.nomeCompleto}</p>
              )}
            </div>

            <div>
              <Label htmlFor="cpf" className="text-sm font-medium text-foreground">
                CPF <span className="text-red-500">*</span>
              </Label>

              <Input
                id="cpf"
                value={userData.cpf}
                readOnly={!isAdminMode}
                onChange={(e) =>
                  handleUserDataChange("cpf", formatCPF(e.target.value))
                }
                className={`mt-2 border-2 transition-colors h-11 focus:ring-2 focus:ring-blue-800/20 ${
                  errors.cpf
                    ? "border-red-400 focus:border-red-600"
                    : isAdminMode
                    ? "border-border focus:border-blue-800"
                    : "bg-muted border-border cursor-not-allowed"
                }`}
              />

              {errors.cpf && (
                <p className="mt-1 text-xs text-red-600">{errors.cpf}</p>
              )}
            </div>
          </>
        )}

        {showBottom && (
          <>
        <div className="md:col-span-2">
          <Label
            htmlFor="falecido"
            className="text-sm font-medium text-foreground"
          >
            O substituído é falecido?
          </Label>
          <RadioGroup
            id="falecido"
            value={userData.falecido ? "sim" : "nao"}
            onValueChange={(value) => {
              handleUserDataChange("falecido", value === "sim");
              if (value === "sim") setIsFalecido(true);
              else setIsFalecido(false);
            }}
            className="flex space-x-4 mt-2"
            disabled={userData.falecido === true && !isAdminMode}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="sim" id="falecido-sim" />
              <Label htmlFor="falecido-sim">Sim</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="nao" id="falecido-nao" />
              <Label htmlFor="falecido-nao">Não</Label>
            </div>
          </RadioGroup>
        </div>

        <div>
          <Label htmlFor="email" className="text-sm font-medium text-foreground">
            E-mail <span className="text-red-500">*</span>
          </Label>

          <Input
            id="email"
            type="email"
            required
            value={userData.email}
            onChange={(e) => handleUserDataChange("email", e.target.value)}
            className={`mt-2 border-2 transition-colors h-11 focus:ring-2 focus:ring-blue-800/20 ${
              errors.email
                ? "border-red-400 focus:border-red-600"
                : "border-border focus:border-blue-800"
            }`}
          />

          {errors.email && (
            <p className="mt-1 text-xs text-red-600">{errors.email}</p>
          )}
        </div>

        <div>
          <Label htmlFor="rg" className="text-sm font-medium text-foreground">
            RG <span className="text-red-500">*</span>
          </Label>

          <Input
            id="rg"
            value={userData.rg || ""}
            onChange={(e) => handleUserDataChange("rg", e.target.value)}
            required
            className={`mt-2 border-2 transition-colors h-11 focus:ring-2 focus:ring-blue-800/20 ${
              errors.rg
                ? "border-red-400 focus:border-red-600"
                : "border-border focus:border-blue-800"
            }`}
          />

          {errors.rg && <p className="mt-1 text-xs text-red-600">{errors.rg}</p>}
        </div>

        <div>
          <Label
            htmlFor="data_nascimento"
            className="text-sm font-medium text-foreground"
          >
            Data de Nascimento <span className="text-red-500">*</span>
          </Label>

          <Input
            id="data_nascimento"
            type="text"
            inputMode="numeric"
            placeholder="DD/MM/AAAA"
            maxLength={10}
            value={dateDisplay}
            onChange={(e) => {
              const masked = maskDateBRLive(e.target.value);
              setDateDisplay(masked);
              if (masked.length === 10) handleUserDataChange("data_nascimento", brToISO(masked));
              else if (!masked) handleUserDataChange("data_nascimento", "");
            }}
            className={`mt-2 border-2 transition-colors h-11 focus:ring-2 focus:ring-blue-800/20 ${
              errors.data_nascimento
                ? "border-red-400 focus:border-red-600"
                : "border-border focus:border-blue-800"
            }`}
          />

          {errors.data_nascimento && (
            <p className="mt-1 text-xs text-red-600">
              {errors.data_nascimento}
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="telefone" className="text-sm font-medium text-foreground">
            Telefone <span className="text-red-500">*</span>
          </Label>

          <Input
            id="telefone"
            type="tel"
            required
            placeholder="(00) 90000-0000"
            value={userData.telefone || ""}
            onChange={(e) =>
              handleUserDataChange("telefone", formatPhoneBRLive(e.target.value))
            }
            className={`mt-2 border-2 transition-colors h-11 focus:ring-2 focus:ring-blue-800/20 ${
              errors.telefone
                ? "border-red-400 focus:border-red-600"
                : "border-border focus:border-blue-800"
            }`}
          />

          {errors.telefone && (
            <p className="mt-1 text-xs text-red-600">{errors.telefone}</p>
          )}
        </div>

        <div>
          <Label
            htmlFor="estadoCivil"
            className="text-sm font-medium text-foreground"
          >
            Estado Civil <span className="text-red-500">*</span>
          </Label>

          <Select
            onValueChange={(value) => handleUserDataChange("estadoCivil", value)}
            value={userData.estadoCivil}
          >
            <SelectTrigger
              className={`w-full mt-2 border-2 h-11 focus:ring-2 focus:ring-blue-800/20 transition-colors ${
                errors.estadoCivil
                  ? "border-red-400 focus:border-red-600"
                  : "border-border focus:border-blue-800"
              }`}
            >
              <SelectValue placeholder="Selecione o estado civil..." />
            </SelectTrigger>

            <SelectContent>
              {estadoCivilOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {errors.estadoCivil && (
            <p className="mt-1 text-xs text-red-600">{errors.estadoCivil}</p>
          )}
        </div>

        {userData.estadoCivil === "Casado(a)" && (
          <>
            <div className="md:col-span-2">
              <Label
                htmlFor="nome_conjuge"
                className="text-sm font-medium text-foreground"
              >
                Nome do Cônjuge <span className="text-red-500">*</span>
              </Label>

              <Input
                id="nome_conjuge"
                value={userData.nome_conjuge || ""}
                onChange={(e) =>
                  handleUserDataChange("nome_conjuge", e.target.value)
                }
                className={`mt-2 border-2 transition-colors h-11 focus:ring-2 focus:ring-blue-800/20 ${
                  errors.nome_conjuge
                    ? "border-red-400 focus:border-red-600"
                    : "border-border focus:border-blue-800"
                }`}
              />

              {errors.nome_conjuge && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.nome_conjuge}
                </p>
              )}
            </div>

            <div>
              <Label
                htmlFor="cpf_conjuge"
                className="text-sm font-medium text-foreground"
              >
                CPF do Cônjuge <span className="text-red-500">*</span>
              </Label>

              <Input
                id="cpf_conjuge"
                value={userData.cpf_conjuge || ""}
                onChange={(e) =>
                  handleUserDataChange("cpf_conjuge", formatCPF(e.target.value))
                }
                className={`mt-2 border-2 transition-colors h-11 focus:ring-2 focus:ring-blue-800/20 ${
                  errors.cpf_conjuge
                    ? "border-red-400 focus:border-red-600"
                    : "border-border focus:border-blue-800"
                }`}
              />

              {errors.cpf_conjuge && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.cpf_conjuge}
                </p>
              )}
            </div>
          </>
        )}
          </>
        )}
      </div>
    </div>
  );
}
